import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, XIcon, WrenchIcon, TrashIcon, ChartBarIcon, PaperclipIcon } from "@phosphor-icons/react";
import FileUploader from "@/components/FileUploader";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const EMPTY_FORM = { producto_id: "", direccion: "salida", cantidad: 1, motivo: "merma", descripcion: "", respaldo_url: "", respaldo_nombre: "", costo_unitario: 0, entry_id: "", bodega_id: "", ubicacion: "" };

const MOTIVOS = [
  { v: "merma", l: "Merma — Pérdida natural o por manejo", cuenta: "6107" },
  { v: "dano", l: "Daño — Producto dañado / no vendible", cuenta: "6107" },
  { v: "vencimiento", l: "Vencimiento — Baja de lote vencido", cuenta: "6107" },
  { v: "robo_extravio", l: "Robo / Extravío — Faltante no justificado", cuenta: "6108" },
  { v: "correccion_conteo", l: "Corrección de conteo — Ajuste tras conteo físico", cuenta: "6109" },
  { v: "muestra_demostracion", l: "Muestra / Demostración — Salida para promoción", cuenta: "6110" },
  { v: "otro", l: "Otro — Texto libre (con respaldo)", cuenta: "6107" },
];

const motivoLabel = (v) => MOTIVOS.find((m) => m.v === v)?.l.split(" — ")[0] || v;

export default function InventoryAdjustments() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState("ajustes");
  const [warehouses, setWarehouses] = useState([]);
  const [lots, setLots] = useState([]);

  // Soporte de prefill desde ExpirationAlerts (motivo "vencimiento")
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("prefill") === "1") {
      try {
        const pre = JSON.parse(localStorage.getItem("prefilledAdjustment") || "null");
        if (pre) {
          setForm({
            ...EMPTY_FORM,
            producto_id: pre.producto_id || "",
            direccion: "salida",
            cantidad: Math.abs(Number(pre.cantidad) || 0),
            motivo: pre.motivo || "vencimiento",
            descripcion: pre.descripcion || "",
            entry_id: pre.entry_id || "",
            bodega_id: pre.bodega_id || "",
          });
          setOpen(true);
          localStorage.removeItem("prefilledAdjustment");
          // limpiar querystring
          window.history.replaceState({}, "", url.pathname);
        }
      } catch { /* tolerable */ }
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: p }, { data: w }] = await Promise.all([
        api.get("/inventory/adjustments"),
        api.get("/products"),
        api.get("/warehouses", { params: { activo: true, include_transito: false } }).catch(() => ({ data: [] })),
      ]);
      setItems(a); setProducts(p); setWarehouses(w || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  // Lotes con saldo del producto seleccionado (para dar de baja un lote específico)
  useEffect(() => {
    if (!form.producto_id) { setLots([]); return; }
    api.get(`/inventory/lots/${form.producto_id}`).then(({ data }) => setLots(data.lotes || [])).catch(() => setLots([]));
  }, [form.producto_id]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.producto_id) { toast.error("Selecciona un producto"); return; }
    if (Number(form.cantidad) <= 0) { toast.error("Cantidad debe ser > 0"); return; }
    if (form.motivo === "otro" && !form.descripcion.trim()) { toast.error("Para 'Otro' la descripción es requerida"); return; }
    const signed = (form.direccion === "salida" ? -1 : 1) * Math.abs(Number(form.cantidad));
    try {
      const payload = {
        producto_id: form.producto_id,
        cantidad: signed,
        motivo: form.motivo,
        descripcion: form.descripcion,
        respaldo_url: form.respaldo_url,
        respaldo_nombre: form.respaldo_nombre || "",
        costo_unitario: Number(form.costo_unitario) || 0,
        entry_id: form.entry_id || null,
        bodega_id: form.bodega_id || null,
        ubicacion: form.ubicacion || "",
      };
      const { data } = await api.post("/inventory/adjustments", payload);
      toast.success(`Ajuste ${data.numero} aplicado — ${fmtL(data.costo_total)}`);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const onVoid = async (a) => {
    if (!window.confirm(`¿Anular ajuste ${a.numero}? Se reversarán los efectos en inventario y contabilidad.`)) return;
    try {
      await api.post(`/inventory/adjustments/${a.id}/void`);
      toast.success("Ajuste anulado");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const am = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: onSave });
  return (
    <Layout
      title="Ajustes de Inventario"
      subtitle="Mermas, daños, vencimientos, robos, correcciones — con asiento contable automático"
      actions={
        <button
          data-testid="new-adjustment-btn"
          onClick={() => { setForm(EMPTY_FORM); setOpen(true); }}
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95"
        >
          <PlusIcon size={16} weight="bold" /> Nuevo ajuste
        </button>
      }
    >
      <div className="border-b border-border mb-4 flex gap-1" data-testid="adjustments-tabs">
        <button onClick={() => setTab("ajustes")} data-testid="tab-ajustes"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 ${tab === "ajustes" ? "border-agro-green text-agro-green font-medium" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
          <WrenchIcon size={15} /> Ajustes
        </button>
        <button onClick={() => setTab("mermas")} data-testid="tab-mermas"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 ${tab === "mermas" ? "border-agro-green text-agro-green font-medium" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
          <ChartBarIcon size={15} /> Reporte de mermas
        </button>
      </div>
      {tab === "mermas" && <MermasReport products={products} />}
      {tab === "ajustes" && (
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="adjustments-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Número</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Producto</th>
                <th className="p-3">Motivo</th>
                <th className="p-3 text-right">Cantidad</th>
                <th className="p-3 text-right">Costo total</th>
                <th className="p-3">Estado</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="8" className="p-12 text-center text-text-muted">
                  <WrenchIcon size={32} className="inline mb-2 opacity-40" />
                  <div>No hay ajustes registrados</div>
                </td></tr>
              ) : items.map((a) => (
                <tr key={a.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`adjustment-row-${a.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{a.numero}</td>
                  <td className="p-3 text-xs text-text-secondary">{fmtDate(a.fecha)}</td>
                  <td className="p-3">
                    <div className="text-sm">{a.producto_codigo}</div>
                    <div className="text-[11px] text-text-muted">{a.producto_nombre}</div>
                  </td>
                  <td className="p-3 text-xs">
                    <div className="text-text-primary">{motivoLabel(a.motivo)}</div>
                    {a.descripcion && <div className="text-[11px] text-text-muted truncate max-w-xs">{a.descripcion}</div>}
                    {(a.lote || a.bodega_codigo) && <div className="text-[10px] text-text-muted">{a.bodega_codigo}{a.lote ? ` · lote ${a.lote}` : ""}</div>}
                    {a.respaldo_url && <a href={a.respaldo_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-agro-yellow inline-flex items-center gap-1"><PaperclipIcon size={10} /> {a.respaldo_nombre || "Respaldo"}</a>}
                  </td>
                  <td className={`p-3 text-right text-sm font-medium ${a.cantidad < 0 ? "text-red-400" : "text-agro-green"}`}>
                    {a.cantidad > 0 ? "+" : ""}{fmtNum(a.cantidad)}
                  </td>
                  <td className="p-3 text-right text-sm">{fmtL(a.costo_total)}</td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-0.5 rounded ${a.estado === "anulado" ? "bg-red-500/10 text-red-400 border border-red-500/30" : "bg-agro-green/10 text-agro-green border border-agro-green/30"}`}>
                      {a.estado}
                    </span>
                  </td>
                  <td className="p-3 pr-5 text-right">
                    {a.estado === "aplicado" && (
                      <button
                        data-testid={`void-adjustment-${a.numero}`}
                        onClick={() => onVoid(a)}
                        className="p-1.5 text-text-muted hover:text-red-400"
                        title="Anular"
                      >
                        <TrashIcon size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...am.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="adjustment-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Nuevo ajuste de inventario</h3>
              <button aria-label="Cerrar" onClick={am.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={am.guardar} className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Producto *</label>
                <select
                  required
                  data-testid="adj-product-select"
                  value={form.producto_id}
                  onChange={(e) => setForm({ ...form, producto_id: e.target.value, entry_id: "" })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                >
                  <option value="">— Seleccionar producto —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.codigo} — {p.sku || p.categoria} (stock: {fmtNum(p.stock_actual)})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Dirección *</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setForm({ ...form, direccion: "salida" })} data-testid="adj-direction-out"
                      className={`flex-1 px-3 py-2 text-sm rounded-md border transition-all ${form.direccion === "salida" ? "border-red-400 text-red-400 bg-red-500/10" : "border-border text-text-secondary hover:border-border/80"}`}>
                      − Salida
                    </button>
                    <button type="button" onClick={() => setForm({ ...form, direccion: "entrada" })} data-testid="adj-direction-in"
                      className={`flex-1 px-3 py-2 text-sm rounded-md border transition-all ${form.direccion === "entrada" ? "border-agro-green text-agro-green bg-agro-green/10" : "border-border text-text-secondary hover:border-border/80"}`}>
                      + Entrada
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Cantidad *</label>
                  <input
                    type="number" min="0.0001" step="any" required
                    data-testid="adj-cantidad"
                    value={form.cantidad}
                    onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Bodega</label>
                  <select value={form.bodega_id} onChange={(e) => setForm({ ...form, bodega_id: e.target.value, entry_id: "" })}
                    data-testid="adj-bodega"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">{form.direccion === "salida" ? "— Todas (PEPS) —" : "— Principal —"}</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo} — {w.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">
                    Lote / ingreso {form.direccion === "salida" ? "(se consume este lote)" : "(copiar datos de lote)"}
                  </label>
                  <select value={form.entry_id} onChange={(e) => setForm({ ...form, entry_id: e.target.value })}
                    data-testid="adj-lote"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">— PEPS automático —</option>
                    {lots.filter((l) => !form.bodega_id || l.bodega_id === form.bodega_id).map((l) => (
                      <option key={l.entry_id} value={l.entry_id}>
                        {l.numero_ingreso || l.fecha_ingreso} · {l.lote || "s/lote"} · {l.bodega_codigo} · saldo {fmtNum(l.cantidad_restante)}{l.fecha_vencimiento ? ` · vence ${l.fecha_vencimiento}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Motivo *</label>
                <select
                  data-testid="adj-motivo"
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                >
                  {MOTIVOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>

              {form.direccion === "entrada" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Costo unitario (entrada)</label>
                  <input
                    type="number" min="0" step="any"
                    data-testid="adj-costo"
                    value={form.costo_unitario}
                    onChange={(e) => setForm({ ...form, costo_unitario: e.target.value })}
                    placeholder="Si vacío, se usa PC PEPS / último costo"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                  />
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">
                  Descripción / Respaldo {form.motivo === "otro" && <span className="text-agro-yellow">(requerida)</span>}
                </label>
                <textarea
                  data-testid="adj-descripcion"
                  rows="2"
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                  placeholder="Detalle de la justificación, número de acta, etc."
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Respaldo (acta, foto, documento)</label>
                <FileUploader folder="evidence" multiple={false} label="Subir respaldo"
                  files={form.respaldo_url ? [{ id: "r", url: form.respaldo_url, original_filename: form.respaldo_nombre || "Respaldo" }] : []}
                  onUploaded={(f) => setForm((cur) => ({ ...cur, respaldo_url: f.url, respaldo_nombre: f.original_filename || "" }))}
                  onRemove={() => setForm((cur) => ({ ...cur, respaldo_url: "", respaldo_nombre: "" }))} />
                <input
                  type="url"
                  data-testid="adj-respaldo"
                  value={form.respaldo_url}
                  onChange={(e) => setForm({ ...form, respaldo_url: e.target.value })}
                  className="mt-2 w-full bg-app border border-border rounded-md px-3 py-2 text-xs focus:border-agro-green"
                  placeholder="…o pega un enlace externo https://"
                />
              </div>

              <div className="text-xs text-text-muted bg-app border border-border rounded-md p-3 leading-relaxed">
                <strong className="text-text-secondary">Asiento contable automático:</strong>
                <br />
                {form.direccion === "salida" ? (
                  <>Debe {MOTIVOS.find((m) => m.v === form.motivo)?.cuenta || "6107"} (gasto) / Haber 1104 (Inventarios)</>
                ) : (
                  <>Debe 1104 (Inventarios) / Haber 4104 (Otros ingresos)</>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={am.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary">Cancelar</button>
                <button disabled={am.saving} type="submit" data-testid="adj-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">Aplicar ajuste</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function MermasReport({ products }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ desde: `${hoy.slice(0, 4)}-01-01`, hasta: hoy, categoria: "", motivo: "" });
  const [data, setData] = useState(null);
  const [agrupar, setAgrupar] = useState("por_motivo");
  const categorias = Array.from(new Set(products.map((p) => p.categoria).filter(Boolean))).sort();

  const run = async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get("/inventory/adjustments/report", { params });
      setData(d);
    } catch (e) { toast.error(apiErr(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  const label = (k) => agrupar === "por_motivo" ? motivoLabel(k) : k;
  return (
    <div className="space-y-4" data-testid="mermas-report">
      <div className="bg-card border border-border/60 rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end print:hidden">
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Desde</label>
          <input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Hasta</label>
          <input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Categoría</label>
          <select value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todas</option>{categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Motivo</label>
          <select value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todos</option>{MOTIVOS.map((m) => <option key={m.v} value={m.v}>{motivoLabel(m.v)}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Agrupar por</label>
          <select value={agrupar} onChange={(e) => setAgrupar(e.target.value)} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" data-testid="mermas-agrupar">
            <option value="por_motivo">Motivo</option><option value="por_categoria">Categoría</option><option value="por_periodo">Período (mes)</option>
          </select></div>
        <div className="flex gap-2">
          <button onClick={run} className="flex-1 bg-agro-green text-black font-medium rounded-md px-3 py-2 text-sm" data-testid="mermas-run">Consultar</button>
          <button onClick={() => window.print()} className="border border-border rounded-md px-3 py-2 text-sm text-text-secondary">Imprimir</button>
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                <tr className="text-left"><th className="p-3 pl-5">Grupo</th><th className="p-3 text-right">Ajustes</th><th className="p-3 text-right">Unidades</th><th className="p-3 pr-5 text-right">Valor</th></tr>
              </thead>
              <tbody>
                {(data[agrupar] || []).map((g) => (
                  <tr key={g.clave} className="border-t border-border/40">
                    <td className="p-3 pl-5">{label(g.clave)}</td><td className="p-3 text-right">{g.ajustes}</td>
                    <td className="p-3 text-right">{fmtNum(g.unidades)}</td><td className="p-3 pr-5 text-right text-red-400">{fmtL(g.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-sidebar font-semibold">
                <tr><td className="p-3 pl-5">TOTAL</td><td className="p-3 text-right">{data.totales.ajustes}</td>
                  <td className="p-3 text-right">{fmtNum(data.totales.unidades)}</td><td className="p-3 pr-5 text-right text-red-400">{fmtL(data.totales.valor)}</td></tr>
              </tfoot>
            </table>
          </div>
          <div className="bg-card border border-border/60 rounded-lg overflow-auto max-h-[480px]">
            <table className="w-full text-xs">
              <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted sticky top-0">
                <tr className="text-left"><th className="p-2 pl-4">Fecha</th><th className="p-2">Producto</th><th className="p-2">Motivo</th><th className="p-2 text-right">Cant.</th><th className="p-2 pr-4 text-right">Costo</th></tr>
              </thead>
              <tbody>
                {data.detalle.map((d) => (
                  <tr key={d.numero} className="border-t border-border/40">
                    <td className="p-2 pl-4">{fmtDate(d.fecha)}</td><td className="p-2">{d.producto_codigo} <span className="text-text-muted">{d.categoria}</span></td>
                    <td className="p-2">{motivoLabel(d.motivo)}</td><td className="p-2 text-right">{fmtNum(d.cantidad)}</td><td className="p-2 pr-4 text-right">{fmtL(d.costo_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
